import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  inject,
  Input,
  output,
  SimpleChanges,
  input,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';

import { VirtualScrollerModule } from '@iharbeck/ngx-virtual-scroller';
import { debounceTime, startWith } from 'rxjs/operators';

// eslint-disable-next-line boundaries/element-types
import { AdminFilterPipe } from '@core/account-logic';
import type { AdditionalUserSelectionOption, User } from '@core/account-types';
import { TranslatePipe } from '@core/i18n-legacy';
import {
  AvatarComponent,
  BadgeComponent,
  BoxComponent,
  ButtonComponent,
  ButtonLoadingDirective,
  CheckboxComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconComponent,
  InputComponent,
  RtTooltip,
  SpinnerComponent,
  TruncateComponent,
} from '@core/user-interface-legacy';

// TODO translations got wrong namespace app, probably should be moved into rtu namespace, so full component could be reused

@Component({
  selector: 'rt-admins-select-dialog-form',
  templateUrl: './admins-select-dialog-form.component.html',
  styleUrls: ['./admins-select-dialog-form.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AvatarComponent,
    BoxComponent,
    ButtonComponent,
    ButtonLoadingDirective,
    CheckboxComponent,
    DialogBodyComponent,
    DialogFooterComponent,
    DialogHeaderComponent,
    DividerComponent,
    EmptyStateComponent,
    FormFieldComponent,
    GroupComponent,
    HeadingComponent,
    IconComponent,
    InputComponent,
    NgTemplateOutlet,
    ReactiveFormsModule,
    RtTooltip,
    SpinnerComponent,
    TranslatePipe,
    VirtualScrollerModule,
    TruncateComponent,
    UpperCasePipe,
    BadgeComponent,
  ],
  providers: [AdminFilterPipe],
})
export class AdminsSelectDialogFormComponent {
  private static VIRTUAL_SCROLL_THRESHOLD = 20;

  private readonly cdRef = inject(ChangeDetectorRef);
  private readonly adminFilterPipe = inject(AdminFilterPipe);
  private readonly destroyRef = inject(DestroyRef);

  public readonly heading = input<string>(undefined!);
  public readonly subheading = input<string>(undefined!);
  public readonly assignments = input<number[]>([]);
  public readonly admins = input<User[]>([]);
  public readonly disallowedUsers = input<User[]>([]);
  public readonly currentAdminId = input<number | null>(undefined!);
  public readonly loading = input<boolean>(false);
  public readonly loadingAdmins = input<boolean>(false);
  public readonly preventAdminRemove = input<boolean>(false);
  public readonly showCountBeforeSubheading = input<boolean>(false);
  public readonly minSelectedAdmins = input<number>(0);
  public readonly maxSelectedAdmins = input<number>(0);
  public readonly lockedAssignments = input<number[]>([]);
  public readonly lockedAssignmentsTooltip = input<string | null>(null);
  public readonly inviteModeEnabled = input(false);
  public readonly disabledUserCopy = input<string>(undefined!);
  public readonly disabledUsersHeader = input<string>(undefined!);
  public readonly additionalUserSelectionOptions = input<AdditionalUserSelectionOption[]>([]);
  public readonly hasActionLabelCount = input<boolean>();
  public readonly withInviteTeamMembersButton = input<boolean>(false);
  public readonly hasStepBack = input<boolean>(false);
  public readonly stepText = input<string>();

  @Input() public actionLabel?: string;

  public selectAllClick = output<void>();
  public selectNoneClick = output<void>();

  public cancelClick = output<void>();
  public submitClick = output<{
    users: User[];
    additionalUserSelectionOptions: AdditionalUserSelectionOption[];
  }>();
  public backClick = output<{
    users: User[];
    additionalUserSelectionOptions: AdditionalUserSelectionOption[];
  }>();
  public inviteTeamMembersClick = output<void>();

  public disabledIds: Record<number, boolean> = {};
  public selectedIds = new Set<number>();
  public selectedAdditionalAssignment = new Set<string>();
  public query = '';
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public virtualScrollUsers: User[] = [];
  public virtualScrollDisabledUsers: User[] = [];
  public virtualScrollAdditionalUserSelectionOptions: AdditionalUserSelectionOption[] = [];
  public virtualScrollItems: Array<User | AdditionalUserSelectionOption> = [];

  public filteredUsers: User[] = this.admins();
  public filteredDisallowedUsers: User[] = this.disallowedUsers();
  public filteredAdditionalUserSelectionOptions: AdditionalUserSelectionOption[] =
    this.additionalUserSelectionOptions();

  public get filteredUsersExceedVirtualScrollThreshold(): boolean {
    return (
      this.filteredUsers.length +
        this.filteredDisallowedUsers.length +
        this.filteredAdditionalUserSelectionOptions.length >
      AdminsSelectDialogFormComponent.VIRTUAL_SCROLL_THRESHOLD
    );
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.assignments &&
      changes.assignments.currentValue &&
      changes.assignments.firstChange
    ) {
      this.selectedIds = new Set(this.assignments());
      this.disabledIds = this.admins().reduce(
        (result, admin) => ({
          ...result,
          [admin.id]:
            this.lockedAssignments().includes(admin.id) ||
            (this.preventAdminRemove() && this.currentAdminId() === admin.id),
        }),
        {},
      );
    }

    if (
      changes.additionalUserSelectionOptions?.currentValue &&
      changes.additionalUserSelectionOptions?.firstChange
    ) {
      this.selectedAdditionalAssignment = new Set(
        this.additionalUserSelectionOptions()
          .filter(option => option.isSelected)
          .map(option => option.key),
      );
    }
  }

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(debounceTime(100), startWith(''), takeUntilDestroyed(this.destroyRef))
      .subscribe((searchControlValue: string) => {
        this.query = searchControlValue;
        this.filteredUsers = this.adminFilterPipe.transform(this.admins(), searchControlValue);
        this.filteredDisallowedUsers = this.adminFilterPipe.transform(
          this.disallowedUsers(),
          searchControlValue,
        );
        this.filteredAdditionalUserSelectionOptions = this.additionalUserSelectionOptions().filter(
          option => option.heading.toLowerCase().includes(searchControlValue.toLowerCase()),
        );
        this.virtualScrollItems = [
          ...this.filteredAdditionalUserSelectionOptions,
          ...this.filteredUsers
            .concat(this.filteredDisallowedUsers)
            .sort((a, _) => (a.hasCandidateAccess ? -1 : 1)),
        ];
        this.cdRef.detectChanges();
      });
  }

  public selectAll(): void {
    this.admins().forEach(admin => {
      this.selectedIds.add(admin.id);
    });
    this.additionalUserSelectionOptions().forEach(option => {
      if (!option.disabled) this.selectedAdditionalAssignment.add(option.key);
    });
    this.selectAllClick.emit();
    this.cdRef.detectChanges();
  }

  public selectNone(): void {
    this.admins().forEach(admin => {
      if (!this.disabledIds[admin.id]) this.selectedIds.delete(admin.id);
    });
    this.additionalUserSelectionOptions().forEach(option => {
      this.selectedAdditionalAssignment.delete(option.key);
    });
    this.selectNoneClick.emit();
    this.cdRef.detectChanges();
  }

  public toggleAssignment(admin: User): void {
    if (this.disabledIds[admin.id]) return;

    this.selectedIds.has(admin.id)
      ? this.selectedIds.delete(admin.id)
      : this.selectedIds.add(admin.id);
    this.cdRef.detectChanges();
  }

  public toggleAdditionalAssignment(option: AdditionalUserSelectionOption): void {
    if (option.disabled) return;

    this.selectedAdditionalAssignment.has(option.key)
      ? this.selectedAdditionalAssignment.delete(option.key)
      : this.selectedAdditionalAssignment.add(option.key);
    this.cdRef.detectChanges();
  }

  public getSelectedCount(): number {
    return this.selectedIds.size + this.selectedAdditionalAssignment.size;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
  }

  public cancel(): void {
    this.cancelClick.emit();
  }

  public isValid(): boolean {
    if (this.preventAdminRemove()) {
      const selectedUsers = this.admins().filter(admin => this.selectedIds.has(admin.id));
      if (!selectedUsers.map(admin => admin.id).includes(<number>this.currentAdminId())) {
        return false;
      }
    }
    const minSelectedAdmins = this.minSelectedAdmins();
    if (minSelectedAdmins && this.getSelectedCount() < minSelectedAdmins) {
      return false;
    }
    const maxSelectedAdmins = this.maxSelectedAdmins();
    if (maxSelectedAdmins && this.getSelectedCount() > maxSelectedAdmins) {
      return false;
    }

    return true;
  }

  public submit(): void {
    this.submitClick.emit(this.buildSelection());
  }

  public goBack(): void {
    this.backClick.emit(this.buildSelection());
  }

  public onVirtualScrollUpdate(event: Array<User | AdditionalUserSelectionOption>) {
    const admins = event.filter(item => 'id' in item);
    this.virtualScrollUsers = admins.filter(
      admin => admin.hasCandidateAccess === true || admin.hasCandidateAccess === undefined,
    );
    this.virtualScrollDisabledUsers = admins.filter(admin => admin.hasCandidateAccess === false);
    this.virtualScrollAdditionalUserSelectionOptions = event.filter(item => 'key' in item);
    this.cdRef.detectChanges();
  }

  private buildSelection(): {
    users: User[];
    additionalUserSelectionOptions: AdditionalUserSelectionOption[];
  } {
    const selectedUsers = this.admins().filter(admin => this.selectedIds.has(admin.id));
    const additionalUserSelectionOptions = this.additionalUserSelectionOptions().map(option => ({
      ...option,
      isSelected: this.selectedAdditionalAssignment.has(option.key),
    }));
    return { users: selectedUsers, additionalUserSelectionOptions };
  }
}
